import { useState } from 'react';
import api from '../api';
import './Dashboard.css';
import './Setup.css';

/** Two steps in one screen: request a code, then enter it with the new
 *  password. Kept together so the email typed in step 1 carries into step 2
 *  without the user re-typing it (and without a route change losing it). */
function ForgotPassword({ onBackToLogin }) {
  const [step, setStep] = useState('request'); // 'request' | 'reset'
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const requestCode = (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    api
      .post('/auth/forgot-password', { email })
      .then((res) => {
        setInfo(res.data.message);
        setStep('reset');
      })
      .catch((err) => setError(err.response?.data?.detail || 'Could not send reset code'))
      .finally(() => setSubmitting(false));
  };

  const submitReset = (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (newPassword !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    api
      .post('/auth/reset-password', { email, otp, new_password: newPassword })
      .then((res) => {
        setDone(true);
        setInfo(res.data.message);
      })
      .catch((err) => setError(err.response?.data?.detail || 'Could not reset password'))
      .finally(() => setSubmitting(false));
  };

  const resendCode = () => {
    setError(null);
    setInfo(null);
    setSubmitting(true);
    api
      .post('/auth/forgot-password', { email })
      .then((res) => setInfo(res.data.message))
      .catch((err) => setError(err.response?.data?.detail || 'Could not resend code'))
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="setup-page">
      <div className="setup-card">
        <div className="setup-brand">
          KROSI<span>FIN</span>
        </div>
        <div className="setup-brand-sub">by NeedCFO</div>
        <h1 className="setup-title">Reset your password</h1>
        <p className="setup-subtitle">
          {done
            ? 'All done.'
            : step === 'request'
            ? "Enter your email and we'll send you a 6-digit reset code."
            : `Enter the code we sent to ${email} and choose a new password.`}
        </p>

        {error && <p className="dash-error">{error}</p>}
        {info && <p style={{ color: '#059669', fontSize: 13, fontWeight: 600 }}>{info}</p>}

        {done ? (
          <div className="dash-btn-row">
            <button className="dash-btn dash-btn-orange" type="button" onClick={onBackToLogin}>
              Back to sign in
            </button>
          </div>
        ) : step === 'request' ? (
          <form onSubmit={requestCode}>
            <div className="field-group">
              <span className="field-label">Email</span>
              <input
                className="field-input"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="dash-btn-row">
              <button className="dash-btn dash-btn-orange" type="submit" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Reset Code'}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={submitReset}>
            <div className="field-group">
              <span className="field-label">Reset Code</span>
              <input
                className="field-input"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
              />
            </div>
            <div className="field-group">
              <span className="field-label">New Password</span>
              <input
                className="field-input"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="field-group">
              <span className="field-label">Confirm New Password</span>
              <input
                className="field-input"
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            <div className="dash-btn-row">
              <button
                className="dash-btn dash-btn-orange"
                type="submit"
                disabled={submitting || otp.length !== 6}
              >
                {submitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>

            <p className="setup-subtitle" style={{ marginTop: 16 }}>
              Didn't get a code?{' '}
              <button type="button" className="setup-link-btn" onClick={resendCode} disabled={submitting}>
                Send again
              </button>
            </p>
          </form>
        )}

        {!done && (
          <p className="setup-subtitle">
            <button type="button" className="setup-link-btn" onClick={onBackToLogin}>
              Back to sign in
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;
